import { useEffect, useSyncExternalStore } from 'react';
import axios from 'axios';
import { Stethoscope } from 'lucide-react';
import baseURL from '../../utils/baseURL';
import { healthIcon } from '../../icons/health';

// Specialty / category icons — the ONE render rule for the whole app.
//
// Super Admin assigns an icon KEY to each specialty and each specialty
// category; the drawing behind the key ships inside this bundle
// (src/icons/health, generated). GET /speciality/icons returns the assignment
// map — { specialties: { SP00003: 'cardiology' }, categories: { SPC00002:
// 'anatomy/heart' } } — and every chip, pill, option row and heading resolves
// through resolveSpecialtyIconKey(): the specialty's own key first, else its
// category's key, else the generic stethoscope.
//
// STYLE IS PLATFORM-WIDE, NEVER PER CALL SITE. The pack ships two drawings per
// key ('outline' and 'solid'); Super Admin picks ONE for the whole platform and
// the same endpoint returns it as `style` (default 'outline'). Two styles must
// never share a screen, so <SpecialtyIcon> does NOT accept a `variant` prop —
// removing it, rather than ignoring it, is deliberate: a mixed call site is now
// a build-time impossibility instead of something a future edit can quietly
// reintroduce. Selection / active state is carried by the chip highlight, the
// tab styling and the text colour — never by the icon's shape.
//
// Deliberately NOT a redux slice. The map is read-only, app-wide, versioned by
// the server (ETag) and needed by a dozen leaf components that mostly do not
// touch the store for anything else; a module-level cache + one hook keeps the
// diff to "add <SpecialtyIcon/> beside <MasterLabel/>" at every render site,
// and the localStorage copy means the icons are right on the very first paint
// after a reload — no spinner, no layout shift when the network answers.
//
// Cache policy: stale-while-revalidate. localStorage 'specialtyIcons::v1' is
// read synchronously at module load; the network is asked ONCE per app start
// (with If-None-Match, so an unchanged map costs a 304 and no body), and
// subscribed mounts re-render only when the version actually changes.

const STORAGE_KEY = 'specialtyIcons::v1';
const EMPTY_MAP = Object.freeze({ version: '', style: 'outline', specialties: {}, categories: {} });

// Deliberately NOT useAxiosAuth. The endpoint is an open read (no token), and
// the shared instance's response interceptor turns any ERR_NETWORK into
// globalErrorHandler.setNetworkError(), which App.jsx answers by replacing the
// whole app with <NetworkErrorPage/>. A decorative icon map must never be able
// to do that: whatever goes wrong here, the page keeps its stethoscopes.
const client = axios.create({ baseURL });

const normalise = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    return {
        version: typeof raw.version === 'string' ? raw.version : '',
        // Anything but an explicit 'solid' — including an entry cached before
        // the platform style existed — is the default, outline.
        style: raw.style === 'solid' ? 'solid' : 'outline',
        specialties: raw.specialties && typeof raw.specialties === 'object' ? raw.specialties : {},
        categories: raw.categories && typeof raw.categories === 'object' ? raw.categories : {},
    };
};

const readStored = () => {
    try {
        return normalise(JSON.parse(localStorage.getItem(STORAGE_KEY)));
    } catch {
        return null;
    }
};

const writeStored = (map) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
        // Quota / private mode — the in-memory copy still serves this session.
    }
};

// Is the API on this page's own origin? Only then do we add our own
// If-None-Match: it is not a CORS-safelisted request header, so cross-origin
// it needs the API's allowlist — current builds have it, but a client that
// ships ahead of its backend would fail the preflight and never refresh the
// map. This app IS cross-origin in local dev and on tenant hosts (the SPA on
// <tenant>.<base>, the API on the base host), and there the browser's HTTP
// cache does the conditional request for us (the server sends Cache-Control:
// public + ETag), which needs no allowlisting.
const apiIsSameOrigin = () => {
    try {
        return new URL(baseURL, window.location.href).origin === window.location.origin;
    } catch {
        return false;
    }
};

// Module-level state shared by every mount.
let current = readStored() || EMPTY_MAP;
let started = false; // the once-per-app-start fetch has been kicked off
const listeners = new Set();

const subscribe = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
};
const getSnapshot = () => current;

const publish = (next) => {
    // Same version → same icons; keep the old object so subscribers skip a render.
    if (next.version && next.version === current.version) return;
    current = next;
    writeStored(next);
    listeners.forEach((fn) => fn());
};

// ONE request per app start, whatever its outcome. `started` flips BEFORE the
// request goes out, so a failure (offline, endpoint not deployed yet, CORS)
// is silent and final for this session: the stored map, or the stethoscope
// fallback, is the right thing to show, and re-asking on every re-render of
// every chip would turn a 404 into a request per keystroke.
const ensureFetched = () => {
    if (started) return;
    started = true;
    // The server compares If-None-Match against its ETag verbatim, which is
    // the QUOTED version — a bare value never matches and never gets a 304.
    const headers = current.version && apiIsSameOrigin() ? { 'If-None-Match': `"${current.version}"` } : {};
    client
        .get('/speciality/icons', {
            headers,
            // 304 is the expected "nothing changed" answer, not an error.
            validateStatus: (s) => s === 200 || s === 304,
        })
        .then((res) => {
            if (res.status !== 200) return;
            const next = normalise(res.data);
            if (next) publish(next);
        })
        .catch(() => {
            // Stale-while-revalidate: keep what we have.
        });
};

// Pure lookup — usable outside React (option builders, tests).
export const resolveSpecialtyIconKey = (specialtyId, categoryId, map = current) =>
    (specialtyId && map.specialties[specialtyId]) || (categoryId && map.categories[categoryId]) || null;

// Subscribes the caller to the icon map and triggers the once-per-start fetch.
export function useSpecialtyIconMap() {
    const map = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    useEffect(() => {
        ensureFetched();
    }, []);
    return map;
}

// <SpecialtyIcon specialtyId categoryId iconKey size className />
//
// iconKey, when the row already carries it (the list endpoints do), wins over
// the map lookup. The style comes from the platform setting and falls back to
// outline when that key has no solid drawing. The stethoscope renders
// immediately for an unknown or unassigned key, so a chip never changes width
// when the map arrives — the icon box is the same size either way.
export default function SpecialtyIcon({
    specialtyId,
    categoryId,
    iconKey,
    size = 16,
    className = '',
    ...rest
}) {
    const map = useSpecialtyIconMap();
    const key = iconKey || resolveSpecialtyIconKey(specialtyId, categoryId, map);
    const Icon = key ? healthIcon(key, map.style) : null;
    const cls = `shrink-0 ${className}`.trim();
    if (Icon) return <Icon size={size} className={cls} {...rest} />;
    return <Stethoscope size={size} className={cls} aria-hidden="true" focusable="false" {...rest} />;
}
